import { useAuthState, useRedirect } from 'react-admin';
import { CustomAppBar } from './components/CustomAppBar';
import { CustomMenu } from './components/CustomMenu';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { ServerBrandProvider, useBrand } from './components/ServerBrandProvider';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { DynamicAdminTitle } from './components/DynamicAdminTitle';
import { TenantErrorState } from './components/TenantErrorState';
import { Box, CircularProgress } from '@mui/material';
import { isGolfOSVariant } from './config/branding';
import { GOLFOS_MENU_WIDTH_PX } from './components/golfos/GolfOSMenu';

interface LayoutContentProps {
  children: React.ReactNode;
  [key: string]: unknown;
}

/** Main column offset to clear fixed sidebar (menu margin + width + content gutter) */
const contentMarginLeft = isGolfOSVariant
  ? `${12 + GOLFOS_MENU_WIDTH_PX + 16}px`
  : '256px';

const LayoutContent = ({ children, ...rest }: LayoutContentProps) => {
  const { brand } = useBrand();
  const { availableTenants, currentTenant, loading, error } = useTenant();
  const { identity, loading: identityLoading } = useAuthState();

  // Check if user is an enterprise admin
  // This is now the ONLY way to identify an enterprise admin
  // Check both identity from React Admin and stored user (fallback)
  const storedUser = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!) : null;
  const isEnterpriseAdmin = (identity as any)?.isEnterpriseAdmin === true || storedUser?.isEnterpriseAdmin === true;

  // Show loading state while fetching tenants or identity
  if (loading || identityLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: brand?.theme?.backgroundColor || '#F5F7FA',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Show error state if no tenants available
  // UNLESS they're an enterprise admin (who doesn't need tenants)
  if ((error || availableTenants.length === 0 || !currentTenant) && !isEnterpriseAdmin) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: brand?.theme?.backgroundColor || '#F5F7FA',
          fontFamily: brand?.fontFamily || 'Roboto',
        }}
      >
        <DynamicAdminTitle />
        <TenantErrorState />
      </Box>
    );
  }

  // Enterprise admin layout - no tenant selector, simplified menu
  if (isEnterpriseAdmin) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          backgroundColor: brand?.theme?.backgroundColor || '#F5F7FA',
          fontFamily: brand?.fontFamily || 'Roboto',
        }}
      >
        <DynamicAdminTitle />
        {/* App bar at the top - no tenant selector for enterprise admin */}
        <CustomAppBar {...rest} hidetenantSelector={true} />

        {/* Main content area with sidebar */}
        <Box
          sx={{
            display: 'flex',
            flex: 1,
          }}
        >
          {/* Side menu */}
          <CustomMenu {...rest} />

          {/* Main content */}
          <Box
            sx={{
              flex: 1,
              padding: '16px',
              marginLeft: contentMarginLeft,
              backgroundColor: brand?.theme?.backgroundColor || '#F5F7FA',
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    );
  }

  // Regular tenant-based layout
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: brand?.theme?.backgroundColor || '#F5F7FA',
        fontFamily: brand?.fontFamily || 'Roboto',
      }}
    >
      <DynamicAdminTitle />
      {/* App bar at the top */}
      <CustomAppBar {...rest} />

      {/* Main content area with sidebar */}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
        }}
      >
        {/* Side menu */}
        <CustomMenu {...rest} />

        {/* Main content */}
        <Box
          sx={{
            flex: 1,
            padding: '16px',
            marginLeft: contentMarginLeft,
            backgroundColor: brand?.theme?.backgroundColor || '#F5F7FA',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

interface CustomLayoutProps {
  children: React.ReactNode;
  [key: string]: unknown;
}

export const CustomLayout = ({ children, ...rest }: CustomLayoutProps) => {
  const { authenticated } = useAuthState();
  const redirect = useRedirect();

  if (!authenticated) {
    redirect('/login');
    return null;
  }

  // Create a completely custom layout without using React Admin's Layout
  return (
    <TenantProvider>
      <ServerBrandProvider>
        <FeatureFlagsProvider>
          <LayoutContent {...rest}>{children}</LayoutContent>
        </FeatureFlagsProvider>
      </ServerBrandProvider>
    </TenantProvider>
  );
};
